import React, { useState, useEffect } from 'react';
import { Coins, Check, X, CreditCard, Zap, Star, Award } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { getUserCoins, updateUserCoins } from '../lib/supabase';
import { Session } from '@supabase/supabase-js';

interface PricingProps {
  session: Session | null;
  onAuthClick: () => void;
}

// Formatear números con separador de miles
const formatNumber = (num: number): string => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

export function Pricing({ session, onAuthClick }: PricingProps) {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState<string>("standard");
  const [loading, setLoading] = useState<boolean>(false);
  const [userCoins, setUserCoins] = useState<number | null>(null);

  // Cargar las monedas del usuario
  useEffect(() => {
    if (session?.user) {
      loadUserCoins();
    }
  }, [session]);

  // Función para cargar las monedas del usuario
  const loadUserCoins = async () => {
    if (!session?.user) return;
    
    try {
      const coins = await getUserCoins(session.user.id);
      if (coins) {
        setUserCoins(coins.balance || 0);
      }
    } catch (error) {
      console.error('Error al cargar las monedas:', error);
    }
  };

  // Función para manejar la compra
  const handlePurchase = async (packageId: string) => {
    if (!session?.user) {
      toast.error('Debes iniciar sesión para comprar monedas');
      onAuthClick();
      return;
    }

    setLoading(true);
    
    try {
      // Aquí iría la lógica de integración con la pasarela de pago
      // Simulamos una compra exitosa
      
      let coinsToAdd = 0;
      let description = '';
      
      switch (packageId) {
        case 'basic':
          coinsToAdd = 100;
          description = 'Compra de paquete Starter';
          break;
        case 'standard':
          coinsToAdd = 300;
          description = 'Compra de paquete Estándar';
          break;
        case 'premium':
          coinsToAdd = 1000;
          description = 'Compra de paquete Advanced';
          break;
        default:
          coinsToAdd = 0;
      }
      
      if (coinsToAdd > 0) {
        // Simulamos la transacción exitosa
        toast.success('Procesando pago...', { duration: 2000 });
        
        setTimeout(async () => {
          // Actualizamos las monedas del usuario
          await updateUserCoins(session.user.id, coinsToAdd, description);
          
          // Recargamos el saldo
          await loadUserCoins();
          
          // Notificamos al usuario
          toast.success(`¡Compra exitosa! Se han añadido ${coinsToAdd} monedas a tu cuenta.`);
          
          // Disparamos eventos para actualizar otros componentes
          window.dispatchEvent(new CustomEvent('coins-updated'));
          
          setLoading(false);
        }, 2500);
      }
    } catch (error) {
      console.error('Error al procesar la compra:', error);
      toast.error('Ha ocurrido un error al procesar tu compra. Por favor, inténtalo de nuevo.');
      setLoading(false);
    }
  };

  // Paquetes de monedas disponibles
  const coinPackages = [
    { 
      id: 'basic', 
      name: 'Starter', 
      coins: 100, 
      price: 3990, 
      discount: 5, 
      cvs: 1,
      features: [
        'Genera 1 CV optimizado',
        'Acceso a 5 plantillas básicas',
        'Análisis de ATS básico',
        'Exportación en PDF'
      ],
      notIncluded: [
        'Plantillas premium',
        'Análisis de ATS avanzado',
        'Optimización de palabras clave',
        'Comparativa con otros candidatos'
      ]
    },
    { 
      id: 'standard', 
      name: 'Estándar', 
      coins: 300, 
      price: 7490, 
      discount: 10, 
      cvs: 3,
      recommended: true,
      features: [
        'Genera 3 CVs optimizados',
        'Acceso a 15 plantillas',
        'Análisis de ATS avanzado',
        'Exportación en PDF y Word',
        'Optimización de palabras clave'
      ],
      notIncluded: [
        'Todas las plantillas premium',
        'Comparativa con otros candidatos'
      ]
    },
    { 
      id: 'premium', 
      name: 'Advanced', 
      coins: 1000, 
      price: 9990, 
      discount: 15, 
      cvs: 10,
      bestValue: true,
      features: [
        'Genera 10 CVs optimizados',
        'Acceso a todas las plantillas',
        'Análisis de ATS avanzado',
        'Exportación en múltiples formatos',
        'Optimización de palabras clave',
        'Comparativa con otros candidatos',
        'Asistencia personalizada'
      ],
      notIncluded: []
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-black py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Encabezado */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 via-purple-200 to-indigo-100 mb-4">
            Planes y Precios
          </h1>
          <p className="text-xl text-purple-200/70 max-w-3xl mx-auto">
            Elige el plan que mejor se adapte a tus necesidades y potencia tu búsqueda de empleo
          </p>
          
          {/* Balance actual si está logueado */}
          {session?.user && userCoins !== null && (
            <div className="mt-8 inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 rounded-full border border-purple-500/30">
              <Coins className="w-4 h-4 text-yellow-400" />
              <span className="text-yellow-400 font-medium">Tu balance actual:</span>
              <span className="text-white font-bold">{formatNumber(userCoins)} monedas</span>
            </div>
          )}
        </div>
        
        {/* Tarjetas de precios */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {coinPackages.map((pkg) => {
            const isPopular = pkg.id === 'standard';
            const isPremium = pkg.id === 'premium';
            
            return (
              <div 
                key={pkg.id} 
                className={`relative rounded-2xl overflow-hidden transition-all duration-300 ${
                  selectedPlan === pkg.id
                    ? 'transform scale-105 z-10 shadow-[0_0_30px_rgba(168,85,247,0.4)]'
                    : 'hover:transform hover:scale-[1.02] shadow-xl'
                }`}
                onClick={() => setSelectedPlan(pkg.id)}
              >
                {/* Borde brillante cuando está seleccionado */}
                <div className={`absolute inset-0 rounded-2xl border-2 transition-colors duration-300 ${
                  selectedPlan === pkg.id
                    ? 'border-purple-500/70 bg-gradient-to-br from-blue-900/80 via-indigo-900/80 to-purple-900/80'
                    : 'border-purple-500/20 bg-gradient-to-br from-blue-900/40 via-indigo-900/40 to-purple-900/40'
                }`}></div>
                
                {/* Efecto de resplandor en la esquina */}
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-purple-500/10 rounded-full blur-xl"></div>
                <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-blue-500/10 rounded-full blur-xl"></div>
                
                {/* Contenido de la tarjeta */}
                <div className="relative z-10 p-6">
                  {/* Encabezado del paquete */}
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-white mb-1">{pkg.name}</h3>
                    <p className="text-sm text-purple-200/70">
                      {pkg.cvs} {pkg.cvs === 1 ? 'CV optimizado' : 'CVs optimizados'}
                    </p>
                    
                    {/* Precio */}
                    <div className="mt-4 flex items-end justify-center">
                      <span className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-purple-300">
                        ${pkg.price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                      </span>
                      <span className="text-sm text-purple-200/70 ml-1 mb-1">CLP</span>
                    </div>
                    
                    {/* Monedas */}
                    <div className="mt-2 flex items-center justify-center gap-1.5">
                      <Coins className="w-4 h-4 text-yellow-400" />
                      <span className="text-xl font-bold text-yellow-400">{pkg.coins}</span>
                      <span className="text-sm text-yellow-400/70">monedas</span>
                    </div>
                    
                    {/* Descuento */}
                    {pkg.discount > 0 && (
                      <div className="mt-2 text-sm inline-flex items-center px-2 py-1 bg-green-900/40 text-green-400 rounded">
                        <Zap className="w-3 h-3 mr-1" />
                        {pkg.discount}% de descuento
                      </div>
                    )}
                  </div>
                  
                  {/* Características incluidas */}
                  <div className="space-y-3 mb-6">
                    <h4 className="text-sm font-medium text-purple-300">Características incluidas:</h4>
                    {pkg.features.map((feature, index) => (
                      <div key={`feature-${index}`} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Características no incluidas */}
                  {pkg.notIncluded && pkg.notIncluded.length > 0 && (
                    <div className="space-y-3 mb-6">
                      <h4 className="text-sm font-medium text-purple-300/70">No incluido:</h4>
                      {pkg.notIncluded.map((feature, index) => (
                        <div key={`not-feature-${index}`} className="flex items-start gap-2">
                          <X className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-400">{feature}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Botón de compra */}
                  <button
                    disabled={loading}
                    onClick={() => handlePurchase(pkg.id)}
                    className={`w-full mt-4 py-3 px-4 rounded-lg flex items-center justify-center gap-2 font-medium transition-all duration-300 ${
                      selectedPlan === pkg.id
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white shadow-lg shadow-purple-500/20'
                        : 'bg-white/10 hover:bg-white/20 text-white'
                    }`}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Procesando...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5" />
                        Comprar ahora
                      </>
                    )}
                  </button>
                </div>
                
                {/* Etiqueta de popular - Corregida la posición */}
                {isPopular && (
                  <div className="absolute top-3 right-3 z-20">
                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1">
                      <Star className="w-3 h-3 fill-current" />
                      POPULAR
                    </div>
                  </div>
                )}
                
                {/* Etiqueta de mejor valor - Corregida la posición */}
                {isPremium && (
                  <div className="absolute top-3 right-3 z-20">
                    <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1">
                      <Award className="w-3 h-3" />
                      MEJOR VALOR
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Preguntas frecuentes */}
        <div className="mt-20 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-purple-200 mb-8 text-center">
            Preguntas Frecuentes
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-blue-900/30 via-indigo-900/30 to-purple-900/30 rounded-xl p-6 border border-purple-500/20">
              <h3 className="text-xl font-medium text-white mb-3">¿Qué son las monedas?</h3>
              <p className="text-purple-200/80">
                Las monedas son la forma de pago dentro de nuestra plataforma. Cada vez que generas un CV optimizado con IA, se descuentan 100 monedas de tu balance.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-blue-900/30 via-indigo-900/30 to-purple-900/30 rounded-xl p-6 border border-purple-500/20">
              <h3 className="text-xl font-medium text-white mb-3">¿Cómo funciona la suscripción?</h3>
              <p className="text-purple-200/80">
                No manejamos suscripciones. Compras paquetes de monedas y las usas cuando necesites generar CVs. Las monedas no caducan y puedes usarlas cuando quieras.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-blue-900/30 via-indigo-900/30 to-purple-900/30 rounded-xl p-6 border border-purple-500/20">
              <h3 className="text-xl font-medium text-white mb-3">¿Puedo solicitar reembolso?</h3>
              <p className="text-purple-200/80">
                Ofrecemos reembolso dentro de los primeros 7 días después de la compra si no has utilizado más del 30% de las monedas adquiridas.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-blue-900/30 via-indigo-900/30 to-purple-900/30 rounded-xl p-6 border border-purple-500/20">
              <h3 className="text-xl font-medium text-white mb-3">¿Qué métodos de pago aceptan?</h3>
              <p className="text-purple-200/80">
                Aceptamos todas las tarjetas de crédito y débito principales (Visa, Mastercard, American Express), además de PayPal y transferencias bancarias.
              </p>
            </div>
          </div>
        </div>
        
        {/* CTA Final */}
        <div className="mt-20 text-center">
          <div className="max-w-3xl mx-auto p-8 rounded-2xl relative overflow-hidden">
            {/* Efectos de fondo */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-indigo-600/20 rounded-2xl"></div>
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-purple-500/10 rounded-full blur-xl"></div>
            <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-blue-500/10 rounded-full blur-xl"></div>
            
            {/* Borde brillante */}
            <div className="absolute inset-0 rounded-2xl border border-purple-500/30"></div>
            
            <div className="relative z-10">
              <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 via-purple-200 to-indigo-100 mb-4">
                ¿Listo para destacar entre la competencia?
              </h2>
              <p className="text-lg text-purple-200/80 mb-8 max-w-2xl mx-auto">
                Consigue el trabajo que mereces con un CV optimizado por IA específicamente para cada empleo al que apliques.
              </p>
              
              <button 
                onClick={() => navigate('/dashboard')}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-lg transition-all duration-300 shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 font-medium text-lg flex items-center gap-2 mx-auto"
              >
                <Zap className="w-5 h-5" />
                Crear mi CV optimizado
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Pricing; 